<template>
	<div
		:id="containerId"
		:class="['editor-container', { 'is-active': isActive }]"
		:style="editorStyle"
	>
		<div
			:id="toolbarId"
			class="editor-toolbar"
		>
			<select class="ql-size">
				<option
					v-for="size in normalizedSizeOptions"
					:key="size"
					:value="size"
				>
					{{ size.replace('px', '') }}
				</option>
			</select>

			<button class="ql-bold">N</button>
			<button class="ql-italic"></button>
			<button class="ql-underline"></button>

			<div
				v-if="!isCompactToolbar"
				class="ql-formats"
			>
				<button
					class="ql-indent"
					value="+1"
				></button>
				<button
					class="ql-indent"
					value="-1"
				></button>
			</div>

			<div
				v-if="!isCompactToolbar"
				class="ql-formats"
			>
				<button
					class="ql-align"
					value=""
				></button>
				<button
					class="ql-align"
					value="center"
				></button>
				<button
					class="ql-align"
					value="right"
				></button>
				<button
					class="ql-align"
					value="justify"
				></button>
			</div>

			<div
				v-if="!isCompactToolbar"
				class="ql-formats"
			>
				<button
					class="ql-list"
					value="ordered"
				></button>
				<button
					class="ql-list"
					value="bullet"
				></button>
			</div>
		</div>
		<quill-editor
			:options="QuillOptions"
			ref="quill"
			@ready="QuillReadyHandler"
			v-model="content"
			spellcheck="false"
		/>
	</div>
</template>

<script>
	import { quillEditor, Quill } from 'vue-quill-editor'

	let editorInstanceCounter = 0

	const Size = Quill.import('attributors/style/size')
	Size.whitelist = [
		'8px',
		'10px',
		'12px',
		'14px',
		'16px',
		'18px',
		'20px',
		'24px',
		'28px',
		'32px',
		'36px',
		'48px',
	]
	Quill.register(Size, true)

	import 'quill/dist/quill.core.css'
	import 'quill/dist/quill.snow.css'
	import 'quill/dist/quill.bubble.css'

	export default {
		name: 'Editor',
		components: { quillEditor },
		props: {
			value: {
				type: String,
				default: '',
			},
			maxlength: {
				type: Number,
				default: null,
			},
			toolbarPreset: {
				type: String,
				default: 'full',
			},
			sizeOptions: {
				type: Array,
				default: () => [
					'8px',
					'10px',
					'12px',
					'14px',
					'16px',
					'18px',
					'20px',
					'24px',
					'28px',
					'32px',
					'36px',
					'48px',
				],
			},
		},
		computed: {
			isCompactToolbar() {
				return this.toolbarPreset === 'stamps'
			},
			editorStyle() {
				const style = {
					'--primary': '#616161',
				}

				if (this.toolbarPreset === 'stamps' && this.isActive) {
					style['--hr'] = 'var(--color-font)'
				}

				return style
			},
			normalizedSizeOptions() {
				const defaultSizes = [
					'8px',
					'10px',
					'12px',
					'14px',
					'16px',
					'18px',
					'20px',
					'24px',
					'28px',
					'32px',
					'36px',
					'48px',
				]

				const validSizes = this.sizeOptions
					.filter(size => typeof size === 'string' && /^(\d+)px$/.test(size))
					.filter((size, index, arr) => arr.indexOf(size) === index)

				if (validSizes.length === 0) {
					return defaultSizes
				}

				return validSizes
			},
		},
		data() {
			const instanceId = ++editorInstanceCounter
			const toolbarId = `toolbar-${instanceId}`
			const containerId = `editor-container-${instanceId}`

			return {
				toolbarId,
				containerId,
				content: this.value,
				isActive: false,
				defaultFontSize: this.toolbarPreset === 'stamps' ? '10px' : '16px',
				primaryColor: { '--primary': '#616161' },
				isResettingContent: false,
				timer: null,
				QuillOptions: {
					modules: {
						toolbar: `#${toolbarId}`,
					},
					theme: 'snow',
					placeholder: '',
				},
			}
		},
		watch: {
			normalizedSizeOptions: {
				handler(sizes) {
					if (!sizes.includes(this.defaultFontSize)) {
						this.defaultFontSize = sizes[0]
					}
				},
				immediate: true,
			},
			value(val) {
				if (val !== this.content) {
					this.content = val
					this.contentFormatted = false
					this.$nextTick(() => {
						if (this.$refs.quill && this.$refs.quill.getLength) {
							this.InitializeContent(this.$refs.quill)
						}
					})
				}
				this.isActive = !!val && val.trim().length > 0
			},
			content(val) {
				this.$emit('input', val)
			},
		},
		methods: {
			FixPickerLabel(quillInstance, size) {
				const toolbar = quillInstance.getModule('toolbar')
				if (toolbar && toolbar.container) {
					const pickerLabel = toolbar.container.querySelector(
						'.ql-picker.ql-size .ql-picker-label'
					)
					if (pickerLabel) {
						pickerLabel.setAttribute('data-value', size)
						pickerLabel.setAttribute(
							'data-label',
							size.replace('px', '')
						)
					}
				}
			},
			GetPickerLabelValue(quillInstance) {
				const toolbar = quillInstance.getModule('toolbar')
				if (toolbar && toolbar.container) {
					const pickerLabel = toolbar.container.querySelector(
						'.ql-picker.ql-size .ql-picker-label'
					)
					if (pickerLabel)
						return pickerLabel.getAttribute('data-value')
				}
				return null
			},
			ApplyDefaultSizeToContent(quillInstance, fontSize) {
				if (!quillInstance || !quillInstance.getLength) return

				const length = quillInstance.getLength()
				if (length <= 1) return

				const delta = quillInstance.getContents()
				let index = 0

				delta.ops.forEach(op => {
					const opLength =
						typeof op.insert === 'string' ? op.insert.length : 1
					if (!op.attributes || !op.attributes.size) {
						quillInstance.formatText(
							index,
							opLength,
							'size',
							fontSize,
							'silent'
						)
					}
					index += opLength
				})

				quillInstance.format('size', fontSize, 'silent')
			},
			GetContentFontSize(delta) {
				for (const op of delta.ops) {
					if (op.attributes && op.attributes.size) {
						return op.attributes.size
					}
				}
				return null
			},
			InitializeContent(quillInstance) {
				if (this.contentFormatted) return

				const htmlContent = this.value || this.content

				if (!htmlContent || quillInstance.getLength() <= 1) {
					this.contentFormatted = true
					quillInstance.format('size', this.defaultFontSize)
					return
				}

				this.contentFormatted = true

				const delta = quillInstance.clipboard.convert(htmlContent)

				const detectedSize =
					this.GetContentFontSize(delta) || this.defaultFontSize

				quillInstance.setContents(delta, 'silent')
				this.ApplyDefaultSizeToContent(quillInstance, detectedSize)
			},
			QuillReadyHandler(quillInstance) {
				this.$refs.quill = quillInstance
				this.contentFormatted = false

				const clipboard = quillInstance.getModule('clipboard')
				if (clipboard) {
					const originalConvert = clipboard.convert.bind(clipboard)
					clipboard.convert = html => {
						const delta = originalConvert(html)
						if (delta && delta.ops) {
							delta.ops = delta.ops.map(op => {
								if (op.attributes) {
									const newAttrs = { ...op.attributes }
									delete newAttrs.color
									delete newAttrs.background
									return { ...op, attributes: newAttrs }
								}
								return op
							})
						}
						return delta
					}
				}

				this.$nextTick(() => {
					this.InitializeContent(quillInstance)
					this.isActive = quillInstance.getText && quillInstance.getText().trim().length > 0
				})

				setTimeout(() => {
					if (quillInstance.getLength() <= 1) {
						this.FixPickerLabel(quillInstance, this.defaultFontSize)
					}
				}, 0)

				quillInstance.on('selection-change', range => {
					const hasText = quillInstance.getText && quillInstance.getText().trim().length > 0
					this.isActive = !!range || hasText

					if (range) {
						this.$emit('focus')
					} else {
						this.$emit('blur')
					}

					if (!range) return
					if (quillInstance.getLength() <= 1) {
						setTimeout(() => {
							const dataValue =
								this.GetPickerLabelValue(quillInstance) ||
								this.defaultFontSize
							this.FixPickerLabel(quillInstance, dataValue)
						}, 0)
					}
				})

				quillInstance.on('text-change', (delta, oldDelta, source) => {
					this.isActive = quillInstance.getText && quillInstance.getText().trim().length > 0

					if (this.maxlength && quillInstance.getLength && (quillInstance.getLength() - 1) > this.maxlength) {
						const extra = (quillInstance.getLength() - 1) - this.maxlength
						const deleteIndex = this.maxlength
						quillInstance.deleteText(deleteIndex, extra, 'api')
						return
					}

					if (source === 'api') {
						this.$nextTick(() => {
							this.InitializeContent(quillInstance)
						})
						return
					}

					if (source === 'user') {
						const range = quillInstance.getSelection()
						if (range && range.length === 0) {
							const [leaf] = quillInstance.getLeaf(range.index)
							if (leaf && !leaf.parent.formats().size) {
								quillInstance.format(
									'size',
									this.defaultFontSize
								)
							}
						}
					}

					if (this.isResettingContent) return

					clearTimeout(this.timer)

					this.timer = setTimeout(() => {
						this.SaveDebounced()
					}, 1000)
				})
			},
			SanitizeContent(content) {
				const isEmptyContent = `<p><br></p>`

				return content.replace(isEmptyContent, '<br>')
			},
			Save() {
				if (
					this.$refs.quill &&
					this.$refs.quill.root &&
					this.$refs.quill.root.innerHTML
				) {
					let content = this.$refs.quill.root.innerHTML

					if (content === '<p><br></p>') {
						content = ''
					}

					this.$emit('save', this.SanitizeContent(content))
				}
			},
			SaveDebounced() {
				const text = this.$refs.quill.getText().trim()

				if (text === '') {
					this.isResettingContent = true

					setTimeout(() => {
						this.$refs.quill.setSelection(0, 0)
						this.$refs.quill.format('size', this.defaultFontSize)
						this.isResettingContent = false
					}, 0)
				}

				this.Save()
			},
			ToolbarFixing() {
				// Bold fixing
				const boldButton = this.$el.querySelector('.ql-bold')
				if (boldButton) {
					boldButton.innerHTML = 'N'
				}
				// Underline fixing
				const underlineButton = this.$el.querySelector('.ql-underline')
				if (underlineButton) {
					underlineButton.innerHTML = 'S'
				}
			},
		},
		mounted() {
			this.ToolbarFixing()
		},
	}
</script>

<style lang="scss" scoped>
	.editor-container {
		height: 100% !important;

		--selectedColor: #2196f3;

		.ql-snow {
			border-color: var(--hr) !important;
		}
		.editor-toolbar.ql-toolbar,
		.editor-toolbar.ql-toolbar.ql-snow,
		:deep(.ql-snow.ql-toolbar),
		:deep(.ql-snow .ql-toolbar) {
			border-color: var(--hr) !important;
			// border: 1px solid var(--hr) !important;
			border-radius: 8px 8px 0 0;
			color: var(--color-font);

			button,
			span.ql-picker {
				box-shadow: unset;
				color: var(--primary);

				.ql-picker {
					color: var(--primary);
				}

				.ql-fill {
					fill: var(--primary);
				}

				.ql-stroke {
					stroke: var(--primary);
				}

				&.ql-active {
					background-color: unset;
					color: var(--selectedColor);

					.ql-picker {
						color: var(--selectedColor);
					}

					.ql-fill {
						fill: var(--selectedColor);
					}

					.ql-stroke {
						stroke: var(--selectedColor);
					}
				}

				&:hover {
					box-shadow: unset;
					background-color: unset;
					color: var(--primary);

					.ql-picker {
						color: var(--fontColor);
					}

					.ql-fill {
						fill: var(--fontColor);
					}

					.ql-stroke {
						stroke: var(--fontColor);
					}
				}
			}

			.ql-font.ql-picker {
				width: 120px;
			}

			.ql-picker {
				&.ql-expanded {
					.ql-picker-label {
						border-color: var(--hr) !important;
					}
				}

				.ql-picker-label {
					&.ql-active {
						background-color: unset;
						color: var(--selectedColor);
					}

					&:hover {
						background-color: unset;
						color: var(--fontColor);
					}
				}

				.ql-picker-options {
					padding: 0;
					border-color: var(--hr) !important;
					color: var(--font-color);

					.ql-stroke {
						color: var(--font-color);
					}

					.ql-picker-item {
						background-color: var(--dark-theme);
						padding: 5px 8px;

						&:hover {
							color: var(--fontColor);
						}

						&.ql-selected {
							color: var(--selectedColor);
						}
					}
				}
			}

			// Toolbar fixing
			.ql-bold,
			.ql-underline {
				font-family: sans-serif;
				font-size: 16px;
				width: 24px;
				height: 24px;
				padding: 0;
				display: flex !important;
				align-items: center !important;
				justify-content: center !important;
			}

			.ql-bold {
				font-weight: bold;
				&:hover {
					color: var(--fontColor);
				}
			}

			.ql-underline {
				text-decoration: underline !important;
				&:hover {
					color: var(--fontColor);
				}
			}

			// Extending QuillJS
			.ql-snow .ql-picker.ql-size .ql-picker-label::before,
			.ql-snow .ql-picker.ql-size .ql-picker-item::before {
				content: attr(data-value);
			}

			.ql-font-roboto {
				font-family: 'Roboto', sans-serif;
			}
			.ql-font-arial {
				font-family: Arial, Helvetica, sans-serif;
			}
			.ql-font-open_sans {
				font-family: 'Open Sans', sans-serif;
			}

			.ql-editor.ql-blank::before {
				left: unset !important;
				right: unset !important;
			}
		}
	}

	:deep(.editor-toolbar.ql-toolbar) {
		box-shadow: unset !important;
		border-color: var(--hr) !important;
	}

	:deep(.ql-container),
	:deep(.ql-container.ql-snow) {
		border: 1px solid var(--hr) !important;
		border-radius: 0 0 8px 8px;
		color: var(--fontColor) !important;
		box-shadow: unset !important;
		padding: 0 !important;
		.ql-editor {
			height: 54vh;
			overflow: auto;
			background-color: unset !important;
			color: var(--color-font) !important;
			caret-color: var(--color-font) !important;

			&.ql-blank::before {
				padding-left: 1rem !important;
			}
		}
	}
</style>
